/** Create / edit dialog for a canonical (react-hook-form + zod + JSON editor).
 *
 *  A canonical is the token aliases normalise names onto, and this is where its
 *  own attributes live. A tag's EFFECTIVE attributes are its own `data` over
 *  this, per key, the tag winning — so a reseller policy written once here
 *  covers every spelling of that reseller, including one ingestion mints later.
 *
 *  `tenant` is clearable: cleared = a system-wide canonical
 *  (`tour_operator: null`); a tenant row shadows the system row with the same
 *  (category, token). No optimistic-concurrency token, so writes are
 *  last-writer-wins. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createCanonical,
  update as updateCanonical,
  type TagCanonicalDetail,
  type TagCanonicalWrite,
} from '@/api/tagCanonicals';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { HelpLink } from '@/components/HelpLink';
import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { CANONICAL_TOKEN_HINT, CANONICAL_TOKEN_RE } from '@/utils/canonicalToken';
import { singlePageTenant } from '@/scope/pageTenants';

import { TAG_CANONICAL_HELP } from './fieldHelp';

const FormSchema = z.object({
  category_code: z.string().trim().min(1, 'Category code is required').max(64),
  token: z
    .string()
    .trim()
    .min(1, 'Canonical token is required')
    .max(128)
    .refine((v) => CANONICAL_TOKEN_RE.test(v.toLowerCase()), CANONICAL_TOKEN_HINT),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { category_code: '', token: '', enabled: true };

function toDefaults(row: TagCanonicalDetail | null): FormValues {
  if (!row) return EMPTY;
  return { category_code: row.category_code, token: row.token, enabled: row.enabled };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  canonical: TagCanonicalDetail | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Preselected category on create (from the grid's category filter). */
  defaultCategory?: string;
  onClose: () => void;
}

export function TagCanonicalFormDialog({
  open,
  canonical,
  pageTenants,
  defaultCategory,
  onClose,
}: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = canonical !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.tag_canonical',
    objectPk: canonical?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['tag_canonicals'] }),
  });

  // Tenant lives outside RHF (self-contained controlled input). null = system-wide.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  // The JSON editor keeps its own undo/cursor state, so its value is read
  // imperatively at submit rather than mirrored into form state.
  const dataRef = useRef<JsonEditorHandle>(null);
  const [dataError, setDataError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(canonical),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    setDataError(null);
    reset(canonical ? toDefaults(canonical) : { ...EMPTY, category_code: defaultCategory ?? '' });
    if (canonical) {
      setTenant(
        canonical.tenant
          ? { id: canonical.tenant.id, name: canonical.tenant.name, code: canonical.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canonical?.id]);

  const mutation = useMutation({
    mutationFn: (body: TagCanonicalWrite) =>
      isEdit ? updateCanonical(canonical.id, body) : createCanonical(body),
    onSuccess: (saved) => {
      snackbar.show(isEdit ? 'Canonical updated.' : 'Canonical created.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_canonicals'] });
      // The schema check is advisory, so a save can succeed WITH problems.
      // Saying so on the way out is the only place the operator would see it.
      if (saved.schema_problems?.length) {
        snackbar.show(
          `Saved, but the data disagrees with the category schema: ${saved.schema_problems[0]}`,
          'warning',
        );
      }
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    let data: Record<string, unknown> | null = null;
    try {
      const raw = dataRef.current?.getValue() ?? {};
      data = Object.keys(raw).length ? raw : null;
    } catch {
      setDataError('Attributes are not valid JSON.');
      return;
    }
    setDataError(null);
    mutation.mutate({
      tour_operator: tenant?.id ?? null,
      category_code: v.category_code.trim(),
      token: v.token.trim().toLowerCase(),
      data,
      enabled: v.enabled,
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        <>
          {isEdit ? `Edit canonical · ~${canonical.token}` : 'New canonical'}{' '}
          <HelpLink story="tag-canonicals" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create canonical'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
        <Box
          component="form"
          onSubmit={submit}
          noValidate
          sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            {...register('category_code')}
            label="Category code"
            size="small"
            fullWidth
            error={Boolean(errors.category_code)}
            helperText={
              errors.category_code?.message ?? 'The tag category this canonical belongs to.'
            }
            InputProps={{ endAdornment: <FieldHelp help={TAG_CANONICAL_HELP.category} /> }}
          />

          <TextField
            {...register('token')}
            label="Canonical token"
            size="small"
            fullWidth
            error={Boolean(errors.token)}
            helperText={
              errors.token?.message ?? 'The governed token aliases normalise onto, e.g. "viator".'
            }
            InputProps={{ endAdornment: <FieldHelp help={TAG_CANONICAL_HELP.token} /> }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <TenantPicker
                value={tenant}
                featured={isEdit ? undefined : pageTenants}
                onChange={setTenant}
                label="Operator"
                placeholder="System / global (no operator)"
              />
            </Box>
            <FieldHelp help={TAG_CANONICAL_HELP.operator} />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {labelWithHelp('Attributes', TAG_CANONICAL_HELP.data)}
            </Typography>
            {isEdit && canonical.schema_problems?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                These attributes disagree with the category schema. That is a warning, not a block —
                the same data written on a tag is not enforced either.
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {canonical.schema_problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </Box>
              </Alert>
            )}
            {dataError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {dataError}
              </Alert>
            )}
            <JsonEditorWidget
              key={`${canonical?.id ?? 'new'}`}
              ref={dataRef}
              initialValue={canonical?.data ?? {}}
              mode="code"
            />
          </Box>

          <Controller
            control={control}
            name="enabled"
            render={({ field }) => (
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={labelWithHelp('Enabled', TAG_CANONICAL_HELP.enabled)}
              />
            )}
          />
        </Box>
      )}
    </AppDialog>
  );
}
